// АНИМ.2: маппинг серверного LeagueOutcome → визуальный event для сцены.
//
// Сервер отдаёт несколько флагов одновременно (promoted + firewallGained),
// мы выбираем ОДИН основной по приоритету. Дополнительные награды
// показываются как часть основной сцены.

import type { MatchFinishResult, MatchLeagueOutcome } from '../../api/client';
import { isBigLeague, leagueByName, type LeagueUiDef } from './leaguesData';

export type LeagueEvent =
  | 'promotion'
  | 'demotion'
  | 'firewall_consumed'
  | 'firewall_gained'
  | 'high_league_entry';

export interface LeagueEventData {
  event: LeagueEvent;
  fromLeague: LeagueUiDef;
  toLeague: LeagueUiDef;
  remainingFirewalls: number;
  // Описание разовой награды (если есть). null если не было или уже забрано.
  rewardDescription: string | null;
}

// Возвращает данные для модалки или null, если показывать нечего.
//
// Приоритет (выбирается ОДНО событие):
//   1. high_league_entry (повышение в Высшую — самое драматичное)
//   2. promotion (любое другое повышение)
//   3. demotion (понижение)
//   4. firewall_consumed (anti-yo-yo или anti-demote)
//   5. firewall_gained (получили +1)
export function extractLeagueEvent(
  finishResult: MatchFinishResult | null
): LeagueEventData | null {
  if (!finishResult) return null;
  const lo: MatchLeagueOutcome | null = finishResult.league;
  if (!lo) return null;

  const rewardDescription =
    finishResult.leaguePromotionReward && !finishResult.leaguePromotionReward.alreadyClaimed
      ? finishResult.leaguePromotionReward.description
      : null;

  // 1. high_league_entry
  if (lo.promoted && isBigLeague(lo.promoted.to)) {
    return {
      event: 'high_league_entry',
      fromLeague: leagueByName(lo.promoted.from),
      toLeague: leagueByName(lo.promoted.to),
      remainingFirewalls: lo.firewalls,
      rewardDescription
    };
  }

  // 2. promotion
  if (lo.promoted) {
    return {
      event: 'promotion',
      fromLeague: leagueByName(lo.promoted.from),
      toLeague: leagueByName(lo.promoted.to),
      remainingFirewalls: lo.firewalls,
      rewardDescription
    };
  }

  // 3. demotion
  if (lo.demoted) {
    return {
      event: 'demotion',
      fromLeague: leagueByName(lo.demoted.from),
      toLeague: leagueByName(lo.demoted.to),
      remainingFirewalls: lo.firewalls,
      rewardDescription: null
    };
  }

  // 4. firewall_consumed
  if (lo.firewallUsedAntiYoyo || lo.firewallUsedAntiDemote) {
    const cur = leagueByName(lo.league);
    return {
      event: 'firewall_consumed',
      fromLeague: cur,
      toLeague: cur,
      remainingFirewalls: lo.firewalls,
      rewardDescription: null
    };
  }

  // 5. firewall_gained
  if (lo.firewallGained) {
    const cur = leagueByName(lo.league);
    return {
      event: 'firewall_gained',
      fromLeague: cur,
      toLeague: cur,
      remainingFirewalls: lo.firewalls,
      rewardDescription: null
    };
  }

  return null;
}
